import React, { useState } from 'react';
import { X, Download, Check, Building2 } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const TEMPLATES = [
  { id: 'classic', label: 'Classique', desc: 'Carre brande avec QR au centre' },
  { id: 'round', label: 'Badge rond', desc: 'Pin rond avec couleur' },
  { id: 'tag', label: 'Etiquette', desc: 'Vertical avec trou de fixation' },
  { id: 'minimal', label: 'Minimal', desc: 'QR + texte simple' },
  { id: 'card', label: 'Carte', desc: 'Format carte de visite' },
];

const StickerPickerModal = ({ open, pingmeId, pingmeName, onClose }) => {
  const [selected, setSelected] = useState('classic');
  const [downloading, setDownloading] = useState(false);
  if (!open) return null;

  const previewUrl = (tpl) => `${API}/pingmes/${pingmeId}/sticker?template=${tpl}&size=400&_=${pingmeId}`;
  const downloadUrl = (tpl) => `${API}/pingmes/${pingmeId}/sticker?template=${tpl}&size=1200`;
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(downloadUrl(selected));
      if (!res.ok) throw new Error('Erreur de generation');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (pingmeName || pingmeId).replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `pingme-${safeName}-${selected}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Sticker telecharge');
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/60 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sticker-picker-modal"
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-charcoal/10 sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-heading font-bold text-lg">Choisir un modele de sticker</h3>
            <p className="text-xs text-charcoal/60">Selectionnez un format puis telechargez le PNG haute resolution.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-surface-alt rounded-lg" data-testid="sticker-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                data-testid={`sticker-template-${t.id}`}
                className={`relative bg-white border-2 rounded-xl p-2 text-left transition-all hover:shadow-brutal hover:-translate-y-0.5 ${
                  selected === t.id ? 'border-primary ring-2 ring-primary/30' : 'border-charcoal/20'
                }`}
              >
                {selected === t.id && (
                  <div className="absolute top-1.5 right-1.5 bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center border-2 border-charcoal">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                )}
                <div className="aspect-square bg-charcoal/5 rounded-lg overflow-hidden flex items-center justify-center">
                  <img
                    src={previewUrl(t.id)}
                    alt={t.label}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <p className="font-bold text-sm text-charcoal mt-2">{t.label}</p>
                <p className="text-[11px] text-charcoal/60">{t.desc}</p>
              </button>
            ))}
          </div>

          <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-3 flex items-center gap-3 mb-4">
            <Building2 className="w-5 h-5 text-charcoal/60 shrink-0" />
            <p className="text-xs text-charcoal/80">
              <strong>Astuce</strong>: si ce PingMe est lie a une organisation, le logo et la couleur sont automatiquement integres au QR code et au design.
            </p>
          </div>

          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="sticker-download-btn"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Generation...' : `Telecharger le sticker (${TEMPLATES.find((x) => x.id === selected)?.label})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickerPickerModal;
